import { FC, useState, useMemo, useRef, useEffect } from "react";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Box, IconButton, Typography } from "@mui/material";

import { NewCustomModalDrawer } from "../../../components/atoms/custom-drawer/new-custom-modal-drawer";
import { Colors } from "../../../configs";
import { pxToRem } from "../../../util";
import { PinTagsSection } from "../../../components/common/pos/assign-pin-tag-modal/pin-tags-section";
import { CustomButton } from "../../../components/atoms";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { cloneDeep, debounce } from "lodash";
import { addSession } from "../../../redux/actions/cartAction";
import { validatePinTagsAction } from "../../../redux/actions/pintagAction";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";

interface IPinTagDrawerProps {
    isOpen: boolean;
    toggleDrawer: () => void;
    containerRef?: HTMLElement | null;
    selectedSessionId: string | null;
    serviceTypeId: number;
    itemsCount: number;
    initialColor?: string;
    initialDate?: string;
    initialTags?: string[];
    onTypingStart?: () => void;
    resetTagsTrigger?: number;
    resetFlipIndex?: number | null;
    setResetFlipIndex?: (index: number | null) => void;
    onCalendarOpenChange?: (isOpen: boolean) => void;
}

const PinTagDrawer: FC<IPinTagDrawerProps> = ({
    isOpen,
    toggleDrawer,
    containerRef,
    selectedSessionId,
    serviceTypeId,
    itemsCount,
    initialColor,
    initialDate,
    initialTags,
    onTypingStart,
    resetTagsTrigger,
    resetFlipIndex,
    setResetFlipIndex,
    onCalendarOpenChange,
}) => {
    const dispatch = useAppDispatch();
    const snackbar = useSnackbarContext();
    const { cart, selectedSession } = useAppSelector((state) => state.cart);
    const { getColorPintags } = useAppSelector((state) => state.pintag);
    const [isCompleted, setIsCompleted] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const previousTagsRef = useRef<Record<string, string[]>>({});

    // Kept in refs (instead of read directly from the closure) so the debounced
    // validator below can stay a SINGLE stable function across re-renders. It
    // reads `cart`/`selectedSession` and also *writes* `cart` (via `addSession`)
    // on success - if those values were closed-over directly, the `useMemo` would
    // have to depend on `cart`, which means every successful validation would
    // invalidate and recreate the debounce instance that validation itself just
    // triggered. That silently discards lodash's pending-timer bookkeeping, so
    // multiple call sites that fire around drawer-open (the initial-color match
    // effect and the due-date match effect in PinTagsSection both call onChange
    // when editing a session that already has a pin tag) end up spawning several
    // independent debounce instances instead of being coalesced into one - each
    // toggling isValidating/isCompleted on its own schedule and stepping on the
    // others, which is what produced the continuous Apply-button flicker.
    const cartRef = useRef(cart);
    const selectedSessionRef = useRef(selectedSession);
    useEffect(() => {
        cartRef.current = cart;
    }, [cart]);
    useEffect(() => {
        selectedSessionRef.current = selectedSession;
    }, [selectedSession]);

    const resolvePinMeta = (
        colorArg: string | null,
        aliasArg?: string,
        colorIdArg?: number
    ) => {
        const list = getColorPintags?.body ?? [];
        const lc = (v?: string | null) => (v ?? "").toLowerCase();

        const meta =
            list.find((t) => lc(t.alias) === lc(aliasArg)) ||
            list.find((t) => lc(t.color) === lc(colorArg)) ||
            list.find((t) => lc(t.description) === lc(aliasArg));

        return {
            color: meta?.color ?? colorArg ?? null,
            alias: meta?.alias ?? aliasArg ?? null,
            colorId: meta?.id ?? colorIdArg,
            day: meta?.day ? String(meta.day).toUpperCase() : undefined,
        };
    };

    const debouncedAssignPinTag = useMemo(
        () =>
            debounce(
                async (
                    color: string | null,
                    tags: string[],
                    textBoxCount: number,
                    colorId?: number,
                    alias?: string,
                    day?: string,
                    date?: string | null
                ) => {
                    if (!selectedSessionId) return;

                    const meta = resolvePinMeta(color, alias, colorId);
                    const targetTagList = tags.slice(0, textBoxCount);
                    const isIncomplete = targetTagList.some((t) => !t || t.length !== 3);

                    setIsValidating(true);
                    setIsCompleted(false);

                    if (meta.color === null || !targetTagList.length || isIncomplete) {
                        setIsValidating(false);
                        return;
                    }

                    // PinTagsSection's seed effects (initial-color match and
                    // due-date match) both re-fire this validator on drawer
                    // open for a session that already has a pin tag assigned,
                    // carrying forward its existing color/tags unchanged. The
                    // backend uniqueness check has no way to know THIS bill
                    // already owns that tag - it just sees the tag currently
                    // assigned to an in-store bill and rejects it as taken,
                    // which cleared the carried-forward tag out from under
                    // the cashier. Skip the round-trip entirely when the
                    // values being submitted exactly match what this session
                    // already has on record; only a real change needs to hit
                    // the backend.
                    const existingSession = cartRef.current.find(
                        (s) => s.sessionId === selectedSessionId
                    );
                    const existingTags = existingSession?.pinTag?.tags ?? [];
                    const isUnchangedFromSaved =
                        (existingSession?.pinTag?.color ?? null) === meta.color &&
                        existingTags.length === targetTagList.length &&
                        existingTags.every((t, i) => t === targetTagList[i]);

                    if (isUnchangedFromSaved) {
                        setIsCompleted(true);
                        setIsValidating(false);
                        return;
                    }

                    try {
                        const res = await dispatch(
                            validatePinTagsAction({
                                pintagColor: meta.color,
                                pintagColorId: meta.colorId,
                                pintagAlias: meta.alias ?? undefined,
                                tags: targetTagList,
                                day: meta.day ?? day,
                            })
                        ).unwrap();

                        const ok = res?.response?.valid;
                        const errorMessage =
                            res?.response?.errorMessage || "Pin tag validation failed";

                        if (!ok) {
                            setIsCompleted(false);
                            const unavailable = res?.response?.unavailableTags?.[0];
                            const invalidIdx = targetTagList.findIndex(
                                (t) => t === unavailable
                            );
                            if (invalidIdx !== -1 && setResetFlipIndex) {
                                setResetFlipIndex(invalidIdx);
                            }
                            snackbar.showSnackbar(errorMessage, "error");
                            return;
                        }

                        const sessionClone = cloneDeep(
                            cartRef.current.find((s) => s.sessionId === selectedSessionId)
                        );
                        if (sessionClone) {
                            sessionClone.pinTag = {
                                color: meta.color,
                                tags: targetTagList,
                                date: date || selectedSessionRef.current?.pinTag?.date || null,
                            };
                            dispatch(addSession(sessionClone));
                            setIsCompleted(true);
                        }
                    } catch {
                        snackbar.showSnackbar("Validation failed", "error");
                        setIsCompleted(false);
                    } finally {
                        setIsValidating(false);
                    }
                },
                300
            ),
        // `cart`/`selectedSession` are intentionally NOT dependencies here - see the
        // cartRef/selectedSessionRef comment above. Keeping this list to values that
        // legitimately identify a different validation target (a different session,
        // a refreshed color list) lets the debounce instance persist across the
        // `addSession` dispatch this same callback triggers.
        [dispatch, selectedSessionId, snackbar, getColorPintags?.body]
    );

    useEffect(() => {
        return () => {
            debouncedAssignPinTag.cancel();
        };
    }, [debouncedAssignPinTag]);

    useEffect(() => {
        if (selectedSessionId) {
            const session = cart.find((s) => s.sessionId === selectedSessionId);
            if (session?.pinTag?.tags) {
                previousTagsRef.current[selectedSessionId] = [...session.pinTag.tags];
            }
        }
    }, [selectedSessionId, cart]);

    const handleTypingStart = () => {
        setIsCompleted(false);
        setIsValidating(true);
        onTypingStart?.();
    };

    const handleCalendarOpenChange = (isOpen: boolean) => {
        setIsCalendarOpen(isOpen);
        onCalendarOpenChange?.(isOpen);
    };

    const handleApply = () => {
        if (isCompleted && !isValidating) {
            toggleDrawer();
        }
    };

    return (
        <NewCustomModalDrawer
            isOpen={isOpen}
            toggleDrawer={toggleDrawer}
            containerRef={containerRef}
            width={pxToRem(600)}
            maxWidth="90vw"
            sx={{
                background: Colors.WHITE,
                minHeight: "100%",
                width: pxToRem(600),
                display: "flex",
                flexDirection: "column",
                padding: pxToRem(20),
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: pxToRem(20),
                    marginTop: pxToRem(20),
                    marginLeft: pxToRem(20),
                    marginRight: pxToRem(10),
                }}
            >
                <Typography
                    sx={{
                        fontSize: pxToRem(40),
                        fontWeight: 600,
                        color: Colors.TEXT_GRAY_DARK,
                    }}
                >
                    Assign Pin Tag
                </Typography>
                <IconButton
                    onClick={toggleDrawer}
                    sx={{ color: Colors.TEXT_GRAY_DARK }}
                >
                    <ArrowForwardIosIcon />
                </IconButton>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: pxToRem(20),
                    marginLeft: pxToRem(25),
                    marginRight: pxToRem(25),
                    overflowY: "auto",
                }}
            >
                {selectedSessionId && (
                    <PinTagsSection
                        key={selectedSessionId}
                        mode="checkin"
                        serviceTypeId={serviceTypeId}
                        initialColor={initialColor}
                        initialDate={initialDate}
                        initialTags={initialTags}
                        itemsCount={itemsCount}
                        screen="checkinSummary"
                        sessionId={selectedSessionId}
                        onChange={debouncedAssignPinTag}
                        isPintagModalSection={true}
                        onTypingStart={handleTypingStart}
                        resetTagsTrigger={resetTagsTrigger}
                        resetFlipIndex={resetFlipIndex}
                        setResetFlipIndex={setResetFlipIndex}
                        onCalendarOpenChange={handleCalendarOpenChange}
                        setIsCompleted={setIsCompleted}
                    />
                )}
            </Box>

            {!isCalendarOpen && (
                <CustomButton
                    sx={{
                        alignSelf: "center",
                        background: Colors.PRIMARY,
                        color: Colors.WHITE,
                        borderRadius: pxToRem(10),
                        width: pxToRem(424),
                        height: pxToRem(60),
                        fontSize: pxToRem(28),
                        marginBottom: pxToRem(15),
                        marginTop: pxToRem(30),
                    }}
                    onClick={handleApply}
                    disabled={!isCompleted || isValidating}
                >
                    Apply
                </CustomButton>
            )}
        </NewCustomModalDrawer>
    );
};

export default PinTagDrawer;
